// Lyra(궁수) 효과음 큐 이름 → 절차 레시피 번호(c022 D4a). 이름은 공통 계약(common.md)의 sfx 큐 10개다.
// 레인 A의 스킬 타임라인(src/shared/skills/lyra.ts, 채널 'sfx')과 기본 공격 발사가 이 이름을 쓴다. 재생은 SynthEngine.cue(이름, x, z).
import { AwakenPhase } from '../../shared/types';
import { Ev, HitFlag, type GameEvent } from '../../shared/events';
import { heroSkills } from '../../shared/skills/registry';
import type { SkillHeroId } from '../../shared/skills/types';
import { Y } from './recipes';

export const LYRA_SFX: Readonly<Record<string, number>> = {
  bow_draw: Y.bowDraw,
  bow_release: Y.bowRelease,
  bow_release_heavy: Y.bowReleaseHeavy,
  arrow_hit: Y.arrowHit,
  arrow_pierce: Y.arrowPierce,
  arrow_rain: Y.arrowRain,
  trap_set: Y.trapSet,
  trap_snap: Y.trapSnap,
  arrow_explode: Y.arrowExplode,
  lyra_awaken: Y.lyraAwaken,
};

/** 계약 이름 목록(시험 고정) */
export const LYRA_SFX_CUES: readonly string[] = [
  'bow_draw', 'bow_release', 'bow_release_heavy', 'arrow_hit', 'arrow_pierce', 'arrow_rain', 'trap_set', 'trap_snap', 'arrow_explode', 'lyra_awaken',
];

/** 이름 → 레시피 번호(없으면 -1) */
export function lyraSfxId(name: string): number {
  const id = LYRA_SFX[name];
  return id === undefined ? -1 : id;
}

/**
 * 게임 이벤트 하나 → 라이라 효과음 큐 이름(없으면 ''). 오디오 엔진이 SynthEngine.cue(이름, e.x, e.z)로 낸다(c022 렌더·오디오 연결).
 * 카엘이면 언제나 '' (카엘 소리는 파일 효과음 decide.ts가 그대로 낸다).
 * - Ev.SkillCue sfx 채널: 라이라 스킬 타임라인 cues[c].name(bow_draw·bow_release·trap_set·arrow_explode …). 세계 위치 큐(flags & 1)도 같은 이름
 * - Ev.Arrow 기본 공격(flags & 2 = 0): bow_release, 3타 강사(종류 1)는 bow_release_heavy. 스킬 화살 발사음은 스킬 큐가 낸다
 * - Ev.Hit 화살 타격(HitFlag.Arrow, 영웅 피격 제외): 관통이면 arrow_pierce, 아니면 arrow_hit. 막힘은 파일 막힘음만
 * - 각성: 모으기 lyra_awaken, 폭풍 화살비 한 파(AwakenSlash) arrow_rain
 */
export function lyraCueOf(e: GameEvent, hero: SkillHeroId | undefined): string {
  if (hero !== 'lyra') return '';
  switch (e.type) {
    case Ev.SkillCue: {
      if (e.b !== 1) return '';
      // flags & 2 = 전직 갈래 1(트랩 레인저, C10-6): 12·13번 큐는 그 갈래 표에서 찾는다
      const tl = heroSkills('lyra', (e.flags & 2) !== 0 ? 1 : 0)[(e.a | 0) - 1];
      const c = tl?.cues[e.c | 0];
      return c && c.channel === 'sfx' && LYRA_SFX[c.name] !== undefined ? c.name : '';
    }
    case Ev.Arrow:
      if ((e.flags & 2) !== 0) return '';
      return (e.d | 0) === 1 ? 'bow_release_heavy' : 'bow_release';
    case Ev.Hit: {
      const f = e.flags | 0;
      if (e.b === 2 || (f & HitFlag.Arrow) === 0 || (f & HitFlag.Blocked) !== 0) return '';
      return (f & HitFlag.Pierce) !== 0 ? 'arrow_pierce' : 'arrow_hit';
    }
    case Ev.AwakenPhase:
      return e.a === AwakenPhase.Gather ? 'lyra_awaken' : '';
    case Ev.AwakenSlash:
      return 'arrow_rain';
    default:
      return '';
  }
}
